// @ts-nocheck
// Call Stats — all data-fetching/state for AgentCallStatsPanel, split out
// so the panel component is just view assembly. Byte-identical fetch shapes
// to the original inline version.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getApiCall } from '@/service/httpCall';
import {
  API_AGENT_DASHBOARD_CALLS,
  API_AGENT_SELF_PERIOD_STATS,
  API_AGENT_SELF_RANGE_STATS,
} from '@/service/url';
import { PRESETS, fillDailyGaps, getPeriodDateRange } from './format';
import { mergeSameCallRows } from '@/features/cti2/utils/dialerFormat';
import { useCallStore } from '@/router/zustand/callStore';
import { readPersisted, usePersistedState, writePersisted } from '@/lib/filters/persisted-state';

const PERIOD_DISPLAY_KEYS = ['today', 'yesterday', 'this_week'];
// Must match CALL_LOG_EXPORT_MAX in the backend agent-controller.
export const CALL_LOG_EXPORT_MAX = 5000;
const CALL_LOG_FIRST_PAGE = { pageIndex: 0, pageSize: 10 };
export const AFTER_CALL_REFETCH_MS = [3000, 10000];
const PERIOD_FALLBACK = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this_week', label: 'This Week' },
];

// Persisted filter keys (shared by every consumer of this hook: the same agent call log).
const PERSIST_KEY = {
  preset: 'cti/callStats:activePreset',
  startDate: 'cti/callStats:startDate',
  endDate: 'cti/callStats:endDate',
  paging: 'cti/callStats:callLogPaging',
  search: 'cti/callStats:callLogSearch',
};

const isValidDate = (d) => d instanceof Date && !Number.isNaN(d.getTime());

// Restored range: a saved preset (e.g. "today") is recomputed from now so it never
// shows stale dates; a custom range ('' preset) comes back as the saved dates.
function restoredRange() {
  const preset = readPersisted(PERSIST_KEY.preset) ?? 'today';
  const match = PRESETS.find((p) => p.key === preset);
  if (match) return match.getRange();
  const start = readPersisted(PERSIST_KEY.startDate);
  const end = readPersisted(PERSIST_KEY.endDate);
  if (isValidDate(start) && isValidDate(end)) return { start, end };
  return { start: moment().startOf('day').toDate(), end: moment().endOf('day').toDate() };
}

export function useCallStatsData(agentId) {
  const [initialRange] = useState(restoredRange);
  const [activePreset, setActivePreset] = usePersistedState(PERSIST_KEY.preset, 'today');
  const [startDate, setStartDate] = useState(() => initialRange.start);
  const [endDate, setEndDate] = useState(() => initialRange.end);
  useEffect(() => {
    writePersisted(PERSIST_KEY.startDate, startDate);
    writePersisted(PERSIST_KEY.endDate, endDate);
  }, [startDate, endDate]);
  const [periodStats, setPeriodStats] = useState<any[]>([]);
  const [periodLoading, setPeriodLoading] = useState(true);
  const [rangeData, setRangeData] = useState<any>(null);
  const [callRecords, setCallRecords] = useState<any[]>([]);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [scatterPeriod, setScatterPeriod] = useState<any>(null);
  const [scatterCalls, setScatterCalls] = useState<any[]>([]);
  const [scatterLoading, setScatterLoading] = useState(false);
  // Call log is server-paged; stat tiles/charts keep their own aggregates (rangeData).
  const [callLogPaging, setCallLogPaging] = usePersistedState(PERSIST_KEY.paging, CALL_LOG_FIRST_PAGE);
  const [callLogTotal, setCallLogTotal] = useState(0);
  // Latest paging/search for fetchCallRecords (loadAll calls it with explicit dates).
  // Seeded from the restored paging/search so the mount fetch uses them.
  const callLogRef = useRef(null);
  if (!callLogRef.current) {
    callLogRef.current = { paging: callLogPaging, search: readPersisted(PERSIST_KEY.search) ?? '', key: '', seq: 0 };
  }

  const statsSeqRef = useRef({ period: 0, range: 0 });

  const fetchPeriodStats = useCallback(async (silent = false) => {
    const seq = ++statsSeqRef.current.period;
    if (!silent) setPeriodLoading(true);
    try {
      const resp: any = await getApiCall(API_AGENT_SELF_PERIOD_STATS);
      if (seq !== statsSeqRef.current.period) return;
      setPeriodStats(Array.isArray(resp?.periods) ? resp.periods : []);
    } catch {
      if (!silent && seq === statsSeqRef.current.period) setPeriodStats([]);
    } finally {
      if (seq === statsSeqRef.current.period) setPeriodLoading(false);
    }
  }, []);

  const fetchRangeStats = useCallback(async (start, end, silent = false) => {
    const seq = ++statsSeqRef.current.range;
    if (!silent) setRangeLoading(true);
    try {
      const startStr = moment(start).format('YYYY-MM-DD');
      const endStr = moment(end).format('YYYY-MM-DD');
      const resp: any = await getApiCall(API_AGENT_SELF_RANGE_STATS, {
        start_date: startStr,
        end_date: endStr,
      });
      if (seq !== statsSeqRef.current.range) return;
      setRangeData(resp || null);
    } catch (error) {
      if (silent || seq !== statsSeqRef.current.range) return;
      toast.error(friendlyApiError(error) || 'Failed to load call statistics');
      setRangeData(null);
    } finally {
      if (seq === statsSeqRef.current.range) setRangeLoading(false);
    }
  }, []);

  const fetchCallRecords = useCallback(async (start, end, silent = false) => {
    if (!agentId) return;
    const startStr = moment(start).format('YYYY-MM-DD');
    const endStr = moment(end).format('YYYY-MM-DD');
    const log = callLogRef.current;
    // New date range or search: back to page 1.
    const key = `${startStr}|${endStr}|${log.search}`;
    if (log.key && key !== log.key && log.paging.pageIndex) {
      log.paging = { ...log.paging, pageIndex: 0 };
      setCallLogPaging(log.paging);
    }
    log.key = key;
    const seq = ++log.seq;
    if (!silent) setRecordsLoading(true);
    try {
      const url = API_AGENT_DASHBOARD_CALLS.replace(':id', agentId);
      const resp: any = await getApiCall(url, {
        start_date: startStr,
        end_date: endStr,
        page: log.paging.pageIndex + 1,
        limit: log.paging.pageSize,
        search: log.search,
      });
      if (seq !== log.seq) return; // a newer page/filter request owns the table
      // One physical call can land as two rows (CDR webhook + this app's own
      // post-hangup write, different call_sids) — the Dialer/right-rail
      // already dedupe this via mergeSameCallRows; the Call Log table never
      // did, so it showed every call twice.
      // ponytail: merged per page, so a pair split across a page edge shows once on each page.
      setCallRecords(Array.isArray(resp?.callRecords) ? mergeSameCallRows(resp.callRecords) : []);
      setCallLogTotal(Number(resp?.total) || 0);
    } catch {
      if (!silent && seq === log.seq) {
        setCallRecords([]);
        setCallLogTotal(0);
      }
    } finally {
      if (seq === log.seq) setRecordsLoading(false);
    }
  }, [agentId]);

  const loadAll = useCallback(
    (start, end, silent = false) => {
      fetchRangeStats(start, end, silent);
      fetchCallRecords(start, end, silent);
    },
    [fetchRangeStats, fetchCallRecords]
  );

  useEffect(() => {
    fetchPeriodStats();
    loadAll(startDate, endDate);
  }, [agentId]);

  const callLogRefreshToken = useCallStore((s) => s.callLogRefreshToken);
  const rangeRef = useRef({ startDate, endDate });
  rangeRef.current = { startDate, endDate };

  const changeCallLogPaging = useCallback((next) => {
    callLogRef.current.paging = next;
    setCallLogPaging(next);
    fetchCallRecords(rangeRef.current.startDate, rangeRef.current.endDate);
  }, [fetchCallRecords]);

  const changeCallLogSearch = useCallback((search) => {
    if (search === callLogRef.current.search) return;
    callLogRef.current.search = search;
    writePersisted(PERSIST_KEY.search, search);
    fetchCallRecords(rangeRef.current.startDate, rangeRef.current.endDate);
  }, [fetchCallRecords]);

  // Whole filtered log (date range + search) for "Export these calls", capped server-side.
  const fetchCallLogExport = useCallback(async () => {
    const url = API_AGENT_DASHBOARD_CALLS.replace(':id', agentId);
    const resp: any = await getApiCall(url, {
      start_date: moment(rangeRef.current.startDate).format('YYYY-MM-DD'),
      end_date: moment(rangeRef.current.endDate).format('YYYY-MM-DD'),
      page: 1,
      limit: CALL_LOG_EXPORT_MAX,
      export: 1,
      search: callLogRef.current.search,
    });
    const raw = Array.isArray(resp?.callRecords) ? resp.callRecords : [];
    return { rows: mergeSameCallRows(raw), total: Number(resp?.total) || 0, fetched: raw.length };
  }, [agentId]);
  useEffect(() => {
    if (!callLogRefreshToken) return undefined;
    const last = AFTER_CALL_REFETCH_MS.length - 1;
    const timers = AFTER_CALL_REFETCH_MS.map((ms, i) => setTimeout(() => {
      const { startDate: s, endDate: e } = rangeRef.current;
      fetchCallRecords(s, e, true);
      if (i === last) {
        fetchPeriodStats(true);
        fetchRangeStats(s, e, true);
      }
    }, ms));
    return () => timers.forEach(clearTimeout);
  }, [callLogRefreshToken]);


  const refreshStats = useCallback(() => Promise.all([
    fetchPeriodStats(true),
    fetchRangeStats(rangeRef.current.startDate, rangeRef.current.endDate, true),
  ]), [fetchPeriodStats, fetchRangeStats]);

  const refreshCallLog = useCallback(
    () => fetchCallRecords(rangeRef.current.startDate, rangeRef.current.endDate, true),
    [fetchCallRecords]
  );

  const totals = rangeData?.totals || {};
  const dailyFilled = useMemo(
    () => fillDailyGaps(rangeData?.daily || [], startDate, endDate),
    [rangeData?.daily, startDate, endDate]
  );

  const periodCards = useMemo(() => {
    const source = periodStats.length ? periodStats : PERIOD_FALLBACK;
    return source.filter((p) => PERIOD_DISPLAY_KEYS.includes(p.key));
  }, [periodStats]);

  const openPeriodScatter = useCallback(
    async (period) => {
      if (!period?.key || !agentId) return;
      setScatterPeriod(period);
      setScatterLoading(true);
      setScatterCalls([]);
      const { start, end } = getPeriodDateRange(period.key);
      try {
        const url = API_AGENT_DASHBOARD_CALLS.replace(':id', agentId);
        const resp: any = await getApiCall(url, {
          start_date: moment(start).format('YYYY-MM-DD'),
          end_date: moment(end).format('YYYY-MM-DD'),
        });
        setScatterCalls(Array.isArray(resp?.callRecords) ? mergeSameCallRows(resp.callRecords) : []);
      } catch {
        setScatterCalls([]);
        toast.error('Failed to load calls for chart');
      } finally {
        setScatterLoading(false);
      }
    },
    [agentId]
  );

  const closePeriodScatter = useCallback(() => {
    setScatterPeriod(null);
    setScatterCalls([]);
  }, []);

  const applyPreset = (presetKey) => {
    const preset = PRESETS.find((p) => p.key === presetKey);
    if (!preset) return;
    const { start, end } = preset.getRange();
    setActivePreset(presetKey);
    setStartDate(start);
    setEndDate(end);
    loadAll(start, end);
  };

  const handleReset = () => {
    const start = moment().startOf('day').toDate();
    const end = moment().endOf('day').toDate();
    setActivePreset('today');
    setStartDate(start);
    setEndDate(end);
    loadAll(start, end);
  };

  const handleRefresh = () => {
    fetchPeriodStats();
    loadAll(startDate, endDate);
  };

  const tableRows = useMemo(
    () => callRecords.map((row, idx) => ({ ...row, key: row.id || idx })),
    [callRecords]
  );

  return {
    activePreset,
    setActivePreset,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    rangeData,
    totals,
    dailyFilled,
    periodCards,
    loadAll,
    periodLoading,
    tableRows,
    callLogPaging: { ...callLogPaging, rowCount: callLogTotal, onChange: changeCallLogPaging },
    changeCallLogSearch,
    fetchCallLogExport,
    loading: periodLoading || rangeLoading,
    recordsLoading,
    scatterPeriod,
    scatterCalls,
    scatterLoading,
    openPeriodScatter,
    closePeriodScatter,
    applyPreset,
    handleReset,
    handleRefresh,
    refreshStats,
    refreshCallLog,
  };
}
